import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Phone, Check, ChevronLeft, Loader2, ShieldCheck, Lock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr, resolveUrl } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseISO = (s) => { const [y, m, dd] = s.split("-").map(Number); return new Date(y, m - 1, dd); };
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

export default function CustomerBooking() {
  const { slug } = useParams();
  const [salon, setSalon] = useState(null);
  const [err, setErr] = useState(false);
  const [step, setStep] = useState(1);
  const [date, setDate] = useState(iso(new Date()));
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [staffId, setStaffId] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [consent, setConsent] = useState(false);
  const [cust, setCust] = useState({ customer_name: "", customer_email: "", customer_phone: "" });
  const [paying, setPaying] = useState(false);
  const [service, setService] = useState(null);

  useEffect(() => {
    api.get(`/public/salon/${slug}`).then((r) => {
      setSalon(r.data);
      document.documentElement.style.setProperty("--gb-primary", r.data.primary_color || "#9A8C73");
      document.documentElement.style.setProperty("--gb-secondary", r.data.secondary_color || "#2C3531");
    }).catch(() => setErr(true));
  }, [slug]);

  useEffect(() => { if (salon?.services?.length) setService((cur) => cur || salon.services[0]); }, [salon]);
  useEffect(() => { setConsent(false); }, [service?.id]);

  useEffect(() => {
    if (!salon) return;
    let active = true;
    const fetchAvail = (silent) => {
      const availSlug = service?.tenant_slug || slug;
      api.get(`/public/salon/${availSlug}/availability`, { params: { date, service_id: service?.id } })
        .then((r) => {
          if (!active) return;
          setSlots(r.data);
          setSlot((cur) => {
            if (cur) {
              const match = r.data.find((s) => s.slot_id === cur.slot_id);
              if (!match || !match.available) {
                if (silent) toast.warning("That time just filled up — please pick another.");
                return null;
              }
            }
            return cur;
          });
        })
        .catch(() => active && setSlots([]));
    };
    setSlot(null);
    setStaffId(null);
    fetchAvail(false);
    const iv = setInterval(() => fetchAvail(true), 7000);
    return () => { active = false; clearInterval(iv); };
  }, [date, salon, slug, service?.id]);

  if (err) return <Center>Salon not found or currently unavailable.</Center>;
  if (!salon) return <Center><Loader2 className="w-6 h-6 animate-spin" /></Center>;

  const primary = salon.primary_color || "#9A8C73";
  const secondary = salon.secondary_color || "#2C3531";
  const consentForm = service?.consent_form || null;
  const services = salon.services || [];
  const balance = service ? (service.total_price - service.deposit_amount).toFixed(2) : "0.00";
  const logoStyle = { height: `${salon.logo_height || 44}px`, width: salon.logo_width ? `${salon.logo_width}px` : "auto", maxHeight: "80px" };

  const pay = async () => {
    setPaying(true);
    try {
      const bookingSlug = service?.tenant_slug || slug;
      const { data } = await api.post(`/public/salon/${bookingSlug}/book`, {
        slot_id: slot.slot_id, date, staff_id: staffId, service_id: service?.id, ...cust,
        consent_accepted: consent, consent_form_id: consentForm?.id || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); setPaying(false); }
  };

  return (
    <div className="min-h-screen grain" style={{ background: "#F7F5F1" }}>
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl backdrop-saturate-150 border-b border-[#EBE8E2]">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {salon.logo_url
              ? <img src={resolveUrl(salon.logo_url)} alt="logo" className="object-contain shrink-0" style={logoStyle} />
              : <div className="h-11 w-11 rounded-xl flex items-center justify-center text-white font-serif font-bold shrink-0" style={{ background: primary }}>{salon.name?.[0] || "S"}</div>}
            <span className="font-serif text-lg md:text-xl font-bold truncate" style={{ color: secondary }}>{salon.name}</span>
          </div>
          <div className="hidden sm:flex items-center gap-5 text-sm text-[#6B6963]">
            {salon.phone && <a href={`tel:${salon.phone}`} className="flex items-center gap-1.5 hover:text-[#2C3531]"><Phone className="w-4 h-4" /> {salon.phone}</a>}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 md:px-8 py-8 md:py-14 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,440px)] gap-8 lg:gap-14 items-start">
        {/* LEFT: salon story */}
        <section className="order-2 lg:order-1 min-w-0">
          <p className="uppercase tracking-[0.2em] text-[11px] font-bold mb-4" style={{ color: primary }}>Book your appointment</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] mb-5" style={{ color: secondary }}>{salon.name}</h1>
          {salon.description && <p className="text-base text-[#5B5954] leading-relaxed max-w-xl mb-8 whitespace-pre-line">{salon.description}</p>}

          <div className="flex flex-wrap gap-3 mb-10">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#E4E0D8] bg-white px-4 py-2 text-sm font-medium"><ShieldCheck className="w-4 h-4" style={{ color: primary }} /> {services.length} service{services.length === 1 ? "" : "s"} available</span>
            {services.length > 0 && <span className="inline-flex items-center gap-2 rounded-full border border-[#E4E0D8] bg-white px-4 py-2 text-sm font-medium">From £{Math.min(...services.map((s) => s.total_price))}</span>}
          </div>

          {(salon.gallery || []).length > 0 && (
            <div className="grid grid-cols-3 gap-3 mb-10" data-testid="salon-gallery">
              {salon.gallery.map((u, i) => (
                <button key={i} onClick={() => setLightbox(resolveUrl(u))} data-testid={`gallery-photo-${i}`}
                  className="group relative aspect-square w-full overflow-hidden rounded-2xl">
                  <img src={resolveUrl(u)} alt={`${salon.name} ${i + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <span className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </button>
              ))}
            </div>
          )}

          {salon.address && (
            <div className="rounded-2xl border border-[#E4E0D8] bg-white overflow-hidden">
              <div className="flex items-start gap-3 p-5">
                <MapPin className="w-5 h-5 mt-0.5 shrink-0" style={{ color: primary }} />
                <div><p className="font-semibold text-sm" style={{ color: secondary }}>Find us</p><p className="text-sm text-[#6B6963]">{salon.address}</p></div>
              </div>
            </div>
          )}
        </section>

        {/* RIGHT: booking widget */}
        <aside className="order-1 lg:order-2 lg:sticky lg:top-24" data-testid="booking-widget">
          <div className="rounded-3xl border border-[#E4E0D8] bg-white overflow-hidden">
            <div className="px-6 pt-6 pb-4">
              <h2 className="font-serif text-xl font-bold" style={{ color: secondary }}>
                {step === 1 ? "Choose your service" : step === 2 ? "Book your appointment" : step === 3 ? "Customer consent" : "Secure your booking"}
              </h2>
              <div className="flex items-center gap-1.5 mt-4">
                {[1, 2, 3, 4].map((s) => (
                  <div key={s} className="flex-1 h-1.5 rounded-full transition-colors" style={{ background: step >= s ? primary : "#E8E6E1" }} />
                ))}
              </div>
            </div>

            <div className="px-6 pb-2 relative overflow-hidden">
              <AnimatePresence mode="wait">
                {step === 1 && (
                  <Slide key="svc">
                    {services.length === 0 && <p className="text-sm text-[#787672] py-6">This salon hasn't published any services yet. Please check back soon.</p>}
                    <div className="space-y-2.5">
                      {services.map((s) => {
                        const on = service?.id === s.id;
                        return (
                          <button key={s.id} onClick={() => setService(s)} data-testid={`service-option-${s.id}`}
                            className="w-full text-left rounded-2xl border p-4 transition-all hover:-translate-y-0.5"
                            style={on ? { borderColor: primary, background: primary + "12" } : { borderColor: "#E8E6E1", background: "#fff" }}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="font-semibold" style={{ color: secondary }}>{s.name}</p>
                                {s.business_name && <p className="text-[11px] font-medium uppercase tracking-wide mt-0.5" style={{ color: primary }}>{s.business_name}</p>}
                                {s.description && <p className="text-xs text-[#787672] mt-0.5 line-clamp-2">{s.description}</p>}
                                {s.duration_min ? <p className="text-xs text-[#9A8C73] mt-1">{s.duration_min} min</p> : null}
                              </div>
                              <div className="text-right shrink-0">
                                <p className="font-serif text-lg font-bold" style={{ color: secondary }}>£{s.total_price}</p>
                                <p className="text-[11px] text-[#787672]">£{s.deposit_amount} deposit</p>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </Slide>
                )}

                {step === 2 && (
                  <Slide key="1">
                    <p className="uppercase tracking-[0.2em] text-[10px] font-bold text-[#787672] mb-2">Select date</p>
                    <div className="flex justify-center mb-5 rounded-2xl border border-[#E8E6E1] bg-white" data-testid="booking-calendar">
                      <Calendar mode="single" selected={parseISO(date)} onSelect={(d) => d && setDate(iso(d))}
                        disabled={{ before: startOfToday() }} showOutsideDays={false}
                        modifiersStyles={{ selected: { backgroundColor: primary, color: "#fff" }, today: { backgroundColor: secondary, color: "#fff", fontWeight: 700 } }}
                        classNames={{ day: "h-9 w-9 p-0 font-normal rounded-full hover:bg-[#F0EFEB] aria-selected:opacity-100", head_cell: "text-[#787672] rounded-md w-9 font-normal text-[0.75rem]" }} />
                    </div>
                    <p className="uppercase tracking-[0.2em] text-[10px] font-bold text-[#787672] mb-2">Available times</p>
                    {(() => {
                      const avail = slots.filter((s) => s.available);
                      return (
                        <div className="grid grid-cols-3 gap-2">
                          {avail.length === 0 && <p className="col-span-3 text-sm text-[#787672] py-4">No availability on this date. Try another day.</p>}
                          {avail.map((s) => {
                            const sel = slot?.slot_id === s.slot_id;
                            return (
                              <button key={s.slot_id} onClick={() => { setSlot(s); setStaffId(null); }} data-testid={`slot-${s.start_time}`}
                                className="py-2.5 px-3 rounded-full text-sm font-semibold transition-all border hover:-translate-y-0.5"
                                style={sel ? { background: primary, color: "#fff", borderColor: primary } : { background: "#fff", borderColor: "#E8E6E1" }}>
                                {s.start_time}
                              </button>
                            );
                          })}
                        </div>
                      );
                    })()}
                    {slot && (slot.staff || []).length > 1 && (
                      <div className="mt-5" data-testid="staff-picker">
                        <p className="uppercase tracking-[0.2em] text-[10px] font-bold text-[#787672] mb-2">Choose your practitioner</p>
                        <div className="flex flex-wrap gap-2">
                          <button onClick={() => setStaffId(null)} data-testid="staff-any"
                            className="px-4 py-2 rounded-full text-sm font-semibold border transition-colors"
                            style={staffId === null ? { background: primary, color: "#fff", borderColor: primary } : { background: "#fff", borderColor: "#E8E6E1" }}>
                            Any available
                          </button>
                          {slot.staff.map((st) => {
                            const on = staffId === st.id;
                            return (
                              <button key={st.id} onClick={() => setStaffId(st.id)} data-testid={`staff-${st.id}`}
                                className="px-4 py-2 rounded-full text-sm font-semibold border transition-colors"
                                style={on ? { background: primary, color: "#fff", borderColor: primary } : { background: "#fff", borderColor: "#E8E6E1" }}>
                                {st.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </Slide>
                )}

                {step === 3 && (
                  <Slide key="2">
                    {consentForm ? (
                      <div className="mb-5" data-testid="consent-form">
                        <p className="font-semibold text-sm mb-1" style={{ color: secondary }}>{consentForm.title}</p>
                        {consentForm.intro && <p className="text-sm text-[#787672] whitespace-pre-line mb-3">{consentForm.intro}</p>}
                        {(consentForm.statements || []).length > 0 && (
                          <ul className="space-y-2 mb-4">
                            {consentForm.statements.map((s, i) => (
                              <li key={i} data-testid={`consent-statement-${i}`} className="flex items-start gap-2 text-sm leading-snug text-[#5B5954]">
                                <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: primary }} />
                                <span>{s}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        <label className="flex items-start gap-3 rounded-xl border p-3 cursor-pointer" style={{ borderColor: consent ? primary : "#E8E6E1", background: consent ? primary + "10" : "#fff" }}>
                          <Checkbox data-testid="consent-accept" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" />
                          <span className="text-sm font-medium leading-snug">I accept and agree to the {consentForm.title}.</span>
                        </label>
                      </div>
                    ) : (
                      <div className="mb-5" data-testid="consent-default">
                        <p className="text-sm text-[#787672] mb-4">Please confirm before booking your appointment.</p>
                        <label className="flex items-start gap-3 rounded-xl border p-3 cursor-pointer" style={{ borderColor: consent ? primary : "#E8E6E1", background: consent ? primary + "10" : "#fff" }}>
                          <Checkbox data-testid="consent-accept" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" />
                          <span className="text-sm font-medium leading-snug">I agree to the terms &amp; conditions and the deposit terms.</span>
                        </label>
                      </div>
                    )}
                    <div className="space-y-3">
                      <div><Label>Full name</Label><Input data-testid="cust-name" value={cust.customer_name} onChange={(e) => setCust({ ...cust, customer_name: e.target.value })} /></div>
                      <div><Label>Email</Label><Input data-testid="cust-email" type="email" value={cust.customer_email} onChange={(e) => setCust({ ...cust, customer_email: e.target.value })} /></div>
                      <div><Label>Phone</Label><Input data-testid="cust-phone" value={cust.customer_phone} onChange={(e) => setCust({ ...cust, customer_phone: e.target.value })} /></div>
                    </div>
                  </Slide>
                )}

                {step === 4 && (
                  <Slide key="3">
                    <p className="text-sm text-[#787672] mb-4">Pay a deposit to confirm your appointment.</p>
                    <div className="rounded-2xl border border-[#E8E6E1] overflow-hidden mb-5">
                      <Row label="Appointment" value={`${date} · ${slot?.start_time}`} />
                      <Row label="Service" value={service?.name} />
                      <Row label="Practitioner" value={staffId ? (slot?.staff?.find((s) => s.id === staffId)?.name || "Assigned") : "Any available"} />
                      <Row label="Total price" value={`£${service?.total_price}`} />
                      <div className="flex justify-between px-4 py-3" style={{ background: primary + "18" }}>
                        <span className="font-semibold" style={{ color: secondary }}>Deposit today</span>
                        <span className="font-bold" style={{ color: primary }}>£{service?.deposit_amount}</span>
                      </div>
                      <Row label="Balance at salon" value={`£${balance}`} />
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[#787672] mb-4">
                      <Lock className="w-3.5 h-3.5" /> Secured by Stripe · Card, Apple Pay & Google Pay
                    </div>
                  </Slide>
                )}
              </AnimatePresence>
            </div>

            <div className="p-6 border-t border-[#F0EFEB] flex gap-3">
              {step > 1 && (
                <Button data-testid="back-btn" variant="outline" onClick={() => setStep(step - 1)} className="rounded-full h-12 px-5"><ChevronLeft className="w-4 h-4" /></Button>
              )}
              {step === 1 && (
                <Button data-testid="to-datetime-btn" disabled={!service} onClick={() => setStep(2)}
                  className="flex-1 rounded-full h-12 font-semibold text-white active:scale-95 transition-transform" style={{ background: secondary }}>Continue</Button>
              )}
              {step === 2 && (
                <Button data-testid="to-step2-btn" disabled={!slot} onClick={() => setStep(3)}
                  className="flex-1 rounded-full h-12 font-semibold text-white active:scale-95 transition-transform" style={{ background: secondary }}>Continue</Button>
              )}
              {step === 3 && (
                <Button data-testid="to-step3-btn" disabled={!consent || !cust.customer_name || !cust.customer_email || !cust.customer_phone} onClick={() => setStep(4)}
                  className="flex-1 rounded-full h-12 font-semibold text-white active:scale-95 transition-transform" style={{ background: secondary }}>Continue to payment</Button>
              )}
              {step === 4 && (
                <Button data-testid="pay-btn" disabled={paying} onClick={pay}
                  className="flex-1 rounded-full h-12 font-semibold text-white active:scale-95 transition-transform" style={{ background: secondary }}>
                  {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : `Pay £${service?.deposit_amount} deposit`}
                </Button>
              )}
            </div>
          </div>
        </aside>
      </main>

      <footer className="border-t border-[#EBE8E2] bg-white/70" data-testid="booking-footer">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 grid md:grid-cols-[1.4fr_1fr] gap-8">
          <div>
            <div className="flex items-center gap-3 mb-3">
              {salon.logo_url
                ? <img src={resolveUrl(salon.logo_url)} alt="logo" className="object-contain" style={{ height: 36, maxHeight: 60 }} />
                : <span className="font-serif text-xl font-bold" style={{ color: secondary }}>{salon.name}</span>}
            </div>
            {salon.address && <p className="text-sm text-[#6B6963] flex items-center gap-1.5"><MapPin className="w-4 h-4 shrink-0" style={{ color: primary }} /> {salon.address}</p>}
            {salon.description && <p className="text-sm text-[#8A887F] mt-3 max-w-md line-clamp-3 whitespace-pre-line">{salon.description}</p>}
          </div>
          <div className="md:text-right text-sm text-[#6B6963] space-y-1">
            <p className="uppercase tracking-[0.2em] text-[11px] font-bold mb-2" style={{ color: primary }}>Contact</p>
            {salon.phone && <a href={`tel:${salon.phone}`} className="block hover:text-[#2C3531] transition-colors">{salon.phone}</a>}
            {salon.email && <a href={`mailto:${salon.email}`} className="block hover:text-[#2C3531] transition-colors">{salon.email}</a>}
          </div>
        </div>
        <div className="border-t border-[#EBE8E2]">
          <div className="max-w-6xl mx-auto px-5 md:px-8 py-4 flex flex-wrap items-center justify-between gap-2 text-xs text-[#9A988F]">
            <span>© {new Date().getFullYear()} {salon.name}. All rights reserved.</span>
            <span>Powered by GlowBook</span>
          </div>
        </div>
      </footer>

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-2xl p-2 bg-transparent border-0 shadow-none" data-testid="gallery-lightbox">
          <DialogTitle className="sr-only">Salon photo</DialogTitle>
          {lightbox && <img src={lightbox} alt="Salon photo" className="w-full h-auto rounded-2xl" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Slide = ({ children }) => (
  <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }}
    transition={{ duration: 0.25 }} className="py-2">{children}</motion.div>
);
const Row = ({ label, value }) => (
  <div className="flex justify-between px-4 py-3 border-b border-[#F0EFEB] last:border-0">
    <span className="text-sm text-[#787672]">{label}</span><span className="text-sm font-semibold text-right">{value}</span>
  </div>
);
const Center = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center text-[#787672] p-6 text-center">{children}</div>
);
